import { useQuery } from "@tanstack/react-query";
import { sanityClient, urlFor } from "@/sanity/client";
import type { GalleryItem } from "./galleryTypes";

// Static fallback imports
import galleryImg1 from "@/assets/gallery-laminate-install.jpg";
import galleryImg2 from "@/assets/gallery-ceramic-install.jpg";
import galleryImg3 from "@/assets/gallery-tile-before.jpg";
import galleryImg4 from "@/assets/gallery-hardwood-after.jpg";
import galleryImg5 from "@/assets/gallery-hardwood-finished.jpg";
import galleryImg6 from "@/assets/gallery-sanding-before.jpg";
import galleryImg7 from "@/assets/gallery-redoak-after.jpg";
import galleryImg8 from "@/assets/gallery-redoak-before.jpg";
import galleryImg9 from "@/assets/gallery-tile-after.jpg";

const staticGalleryItems: GalleryItem[] = [
  { type: "paired", photos: [
    { img: galleryImg5, alt: "Finished hardwood floor installation", caption: "Hardwood Installation", category: "Hardwood" },
    { img: galleryImg2, alt: "Ceramic tile installation in progress", caption: "Ceramic Tile Install", category: "Ceramic & Tile" },
  ]},
  { type: "beforeAfter", before: { img: galleryImg6, alt: "Floor before sanding process", caption: "Before Sanding" }, after: { img: galleryImg4, alt: "Hardwood floor after shellac and polyurethane", caption: "After — Shellac & Polyurethane" }, category: "Hardwood" },
  { type: "beforeAfter", before: { img: galleryImg8, alt: "Floor before red oak installation", caption: "Before — Red Oak" }, after: { img: galleryImg7, alt: "Pre-finished red oak installation", caption: "After — Red Oak" }, category: "Hardwood" },
  { type: "beforeAfter", before: { img: galleryImg3, alt: "Bathroom tile before installation", caption: "Before — Bathroom Tile" }, after: { img: galleryImg9, alt: "Bathroom tile after installation", caption: "After — Bathroom Tile" }, category: "Ceramic & Tile" },
  { type: "single", img: galleryImg1, alt: "Laminate flooring installation", caption: "Laminate Install", category: "Laminate" },
];

interface SanityGalleryImage {
  _id: string;
  title: string;
  image: unknown;
  category: string;
  alt?: string;
  caption?: string;
  isBefore?: boolean;
  isAfter?: boolean;
  pairId?: string;
  order?: number;
}

const GALLERY_QUERY = `*[_type == "galleryImage"] | order(order asc) {
  _id, title, image, "category": category->title, alt, caption, isBefore, isAfter, pairId, order
}`;

const CATEGORIES_QUERY = `*[_type == "galleryCategory"] | order(order asc) { _id, title }`;

function transformSanityToGallery(images: SanityGalleryImage[]): GalleryItem[] {
  const items: GalleryItem[] = [];
  const pairedIds = new Set<string>();

  // Process before/after pairs first
  const beforeImages = images.filter((img) => img.isBefore && img.pairId);
  for (const before of beforeImages) {
    const after = images.find((img) => img.isAfter && img.pairId === before.pairId);
    if (after) {
      pairedIds.add(before._id);
      pairedIds.add(after._id);
      items.push({
        type: "beforeAfter",
        before: {
          img: urlFor(before.image).width(800).url(),
          alt: before.alt || before.title,
          caption: before.caption || before.title,
        },
        after: {
          img: urlFor(after.image).width(800).url(),
          alt: after.alt || after.title,
          caption: after.caption || after.title,
        },
        category: before.category,
      });
    }
  }

  // Remaining singles
  const singles = images.filter((img) => !pairedIds.has(img._id) && !img.isAfter);
  for (const img of singles) {
    items.push({
      type: "single",
      img: urlFor(img.image).width(800).url(),
      alt: img.alt || img.title,
      caption: img.caption || img.title,
      category: img.category,
    });
  }

  return items;
}

export function useGalleryItems() {
  const { data: sanityImages, isLoading } = useQuery({
    queryKey: ["sanity-gallery"],
    queryFn: () => sanityClient.fetch<SanityGalleryImage[]>(GALLERY_QUERY),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const { data: sanityCategories } = useQuery({
    queryKey: ["sanity-gallery-categories"],
    queryFn: () => sanityClient.fetch<{ _id: string; title: string }[]>(CATEGORIES_QUERY),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const items: GalleryItem[] =
    sanityImages && sanityImages.length > 0
      ? transformSanityToGallery(sanityImages)
      : staticGalleryItems;

  const categories: string[] =
    sanityCategories && sanityCategories.length > 0
      ? sanityCategories.map((c) => c.title)
      : ["Hardwood", "Ceramic & Tile", "Laminate"];

  return { items, categories, isLoading, isFromSanity: !!sanityImages?.length };
}
